import { Component, Input, Output, EventEmitter, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ImageService } from './../../dashboard/program-list/image.service';
import { VideoUploadService } from '../services/video-upload.service';

@Component({
  selector: 'app-upload-handler',
  templateUrl: './upload-handler.component.html',
  styleUrls: ['./upload-handler.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadHandlerComponent),
      multi: true
    }
  ]
})
export class UploadHandlerComponent implements ControlValueAccessor {
  @Input() pickerId: string;
  @Input() buttonText: string;
  @Input() fileOpts: boolean;
  @Input() uploadType: string; // type of media for s3 upload, else null for server upload
  @Input() currentMediaUrl: string; // media to show a thumbnail of
  @Output() postFile: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('fileInput') fileInput;

  public nameOfFile: string;
  public inProgress: boolean = false;

  private propagateChange: (_: any) => void;
  private propagateTouch: () => void;

  constructor(
    private imageService: ImageService,
    private videoUploadService: VideoUploadService
  ) {}

  public whatChanged(event: Event): void {
    event.stopPropagation();
    const fi = this.fileInput.nativeElement;
    if (fi.files && fi.files[0]) {
      const fileToUpload = fi.files[0];
      this.nameOfFile = fileToUpload.name;

      if (this.checkIfSupportedMedia(fileToUpload.name)) {
        this.handleFileUpload(fileToUpload);
      } else {
        this.currentMediaUrl = null;
      }

      this.markAsTouched();
    }
  }

  private handleFileUpload(file: File): void {
    if (!file || !file.type) {
      console.error("Invalid file or file type.");
      return;
    }

    this.inProgress = true;

    if (this.isVideo(file)) {
      this.uploadFile(file, 'video');
    } else if (this.isImage(file)) {
      this.uploadFile(file, 'image');
    } else {
      this.emitFile(file);
    }
  }

  private isVideo(file: File): boolean {
    return file.type && file.type.startsWith('video/');
  }

  private isImage(file: File): boolean {
    return file.type && file.type.startsWith('image/');
  }

  public checkIfImage(url: string): boolean {
    return /\.(jpg|jpeg|png|svg|gif)$/i.test(url);
  }
  
  public checkIfVideo(url: string): boolean {
    return /\.(mp4|m4v|mov|webm|mpg|avi|ogv|3gp|3g2)$/i.test(url);
  }

  public checkIfSupportedMedia(url: string): boolean {
    return this.checkIfImage(url) || this.checkIfVideo(url);
  }  

  private uploadFile(file: File, fileType: string): void {
    if (this.uploadType) {
      this.awsPostFile(file, this.uploadType, fileType);
    } else {
      this.emitFile(file);
    }
  }

  private awsPostFile(file: File, uploadType: string, fileType: string): void {
    const postData = {
      mime_type: file.type,
      upload_type: uploadType,
      size: file.size
    };

    if (fileType === 'video') {
      this.uploadVideo(file, postData);
    } else if (fileType === 'image') {
      this.uploadImage(file, postData);
    }
  }

  private uploadVideo(file: File, postData: any): void {
    this.videoUploadService.postVideo(file).subscribe(
      response => this.handleUploadSuccess(response, 'video'),
      error => this.handleUploadError(error)
    );
  }

  private uploadImage(file: File, postData: any): void {
    this.imageService.postImage(postData, file).subscribe(
      response => this.handleUploadSuccess(response, 'image'),
      error => this.handleUploadError(error)
    );
  }

  private handleUploadSuccess(response: any, fileType: string): void {
    const formInput = this.createFormInput(response, fileType);
    this.emitFile(formInput);
    this.currentMediaUrl = response.location;
    this.inProgress = false;
  }

  private createFormInput(response: any, fileType: string): Object {
    if (fileType === 'video') {
      return { videoId: response.id, uploadType: this.uploadType };
    } else {
      return { key: response.key, uploadType: this.uploadType };
    }
  }

  private handleUploadError(error: any): void {
    console.error(error);
    this.inProgress = false;
  }

  private emitFile(file: any): void {
    this.postFile.emit(file);
    if (this.propagateChange) {
      this.propagateChange(file);
    }
  }

  private markAsTouched(): void {
    if (this.propagateTouch) {
      this.propagateTouch();
    }
  }

  public writeValue(value: any): void {
    if (value) {
      this.nameOfFile = value.name || '';
      this.currentMediaUrl = value.url || value;
    }
  }

  public registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {

  }

  public removeMedia(): void {
    this.currentMediaUrl = null;
    this.nameOfFile = null;
    this.emitFile(null);
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/shared/upload-handler/upload-handler.component.ts